import { useEffect, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '../../../contexts/hooks';
import { sessionManager } from '../../../utils/session-manager';
import { setIsAuthenticated } from '../../../contexts/slices/config';
import { clearFilteredOrders, clearOrder, clearSelectedOrders } from '../../../contexts/slices/orders';
import { logOut as logOutRdx } from '../../../contexts/slices/auth';
import { clearAllNotifications } from '../../../contexts/slices/notifications';
import { clearQuotation } from '../../../contexts/slices/quotations';
import { setDashboards } from '../../../contexts/slices/dashboards';
import { useNavigate } from 'react-router-dom';
import { newToast } from '../..';

/**
 * Hook robusto para gestión de sesión con sincronización entre pestañas
 */
const useSessionManager = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector((x) => x.configuration.IsAuthenticated);
  const sessionExpired = parseInt(
    useAppSelector((x) => x.configuration.Data?.Config_General.find((x) => x.Cgn_Nombre === 'SessionExpired'))?.Cgn_Resultado || '10'
  );
  const userId = useAppSelector((x) => x.userInfo.User?.Us_Id);
  const callbacksRegistered = useRef(false);

  // ⚡ Validación SÍNCRONA en el primer render — antes de cualquier useEffect.
  // Esto garantiza que se lee el heartbeat ANTES de que initializeSession lo sobreescriba.
  const loadValidation = useRef(sessionManager.validateSessionOnLoad());

  // Función para limpiar el estado de la aplicación
  const clearApplicationState = () => {
    dispatch(setIsAuthenticated(false));
    dispatch(logOutRdx());
    dispatch(clearOrder());
    dispatch(clearQuotation());
    dispatch(clearSelectedOrders());
    dispatch(clearAllNotifications());
    dispatch(setDashboards([]));
    dispatch(clearFilteredOrders());
  };

  // Función para manejar el logout
  const handleLogout = () => {
    clearApplicationState();
    navigate('/login');    
  };

  // Función para manejar la expiración de sesión
  const handleSessionExpired = () => {
    clearApplicationState();
    navigate('/login');
    newToast('WARNING', 'Su sesión ha expirado por inactividad', 5);
  };

  // Función para manejar la restauración de sesión
  const handleSessionRestored = () => {
    // Verificar si realmente necesitamos restaurar algo
    const sessionState = sessionManager.getSessionState();
    if (sessionState && sessionState.isAuthenticated && !isAuthenticated) {
      // Aquí podrías implementar lógica para restaurar la sesión
      // Por ejemplo, recargar la página o actualizar el estado
      window.location.reload();
    }
  };

  // Inicializar la sesión cuando el usuario se autentica
  useEffect(() => {
    // NO inicializar si la validación al cargar determinó que la sesión debe cerrarse.
    // Esto evita que initializeSession() sobreescriba el heartbeat y la sesión invalidada.
    if (loadValidation.current === 'closed' || loadValidation.current === 'expired') {
      return;
    }

    if (isAuthenticated && !callbacksRegistered.current) {
      // Inicializar el session manager
      sessionManager.initializeSession(sessionExpired, userId?.toString());

      // Registrar callbacks
      sessionManager.onLogout(handleLogout);
      sessionManager.onSessionExpired(handleSessionExpired);
      sessionManager.onSessionRestored(handleSessionRestored);

      callbacksRegistered.current = true;
    }
  }, [isAuthenticated, sessionExpired, userId]);

  // Limpiar callbacks cuando el componente se desmonta o el usuario se desloguea
  useEffect(() => {
    return () => {
      if (callbacksRegistered.current) {
        sessionManager.clearCallbacks();
        callbacksRegistered.current = false;
      }
    };
  }, []);

  // Manejar resultado de validación de sesión al cargar la página.
  // Usa el resultado calculado sincrónicamente en loadValidation.current.
  useEffect(() => {
    const result = loadValidation.current;

    switch (result) {
      case 'valid':
        // Sesión válida (refresh de pestaña o hay otra pestaña activa)
        if (!isAuthenticated) {
          handleSessionRestored();
        }
        break;
      case 'expired':
        // La sesión expiró por inactividad — limpiar siempre
        clearApplicationState();
        navigate('/login');
        newToast('WARNING', 'Su sesión ha expirado por inactividad', 5);
        // Marcar como procesado para que no interfiera con un login posterior
        loadValidation.current = 'none';
        break;
      case 'closed':
        // Todas las pestañas fueron cerradas → forzar login por seguridad
        clearApplicationState();
        navigate('/login');
        newToast('INFO', 'Sesión cerrada por seguridad: se cerraron todas las pestañas del navegador', 5);
        loadValidation.current = 'none';
        break;
      case 'none':
        // No hay sesión almacenada, no hacer nada
        break;
    }
  }, []);

  // Función para cerrar sesión manualmente
  const logout = async () => {
    await sessionManager.logout();
    // El callback handleLogout se ejecutará automáticamente
  };

  // Función para verificar si la sesión es válida
  const isSessionValid = () => {
    return sessionManager.isSessionValid();
  };

  // Función para obtener información de la sesión
  const getSessionInfo = () => {
    return sessionManager.getSessionState();
  };

  return {
    logout,
    isSessionValid,
    getSessionInfo,
    sessionExpired
  };
};

export default useSessionManager;
